import * as THREE from 'three';
import Assets from './Assets.js';

export default class Dressing {
  static instance;

  constructor() {
    if (Dressing.instance) {
      return Dressing.instance;
    }
    Dressing.instance = this;

    this.assets = new Assets();
    this.textures = {};
    this.scene;
    this.content = [];
    this.defaultMap = null;
    this.refreshUICurrentBox;
    this.getUIid;

    this.newGlobalColor(5);
  }

  //------------------//
  // INSTANCE CONTENT //
  //------------------//
  resetInstance() {
    this.content = [];
  }

  restoreInstance(configuration) {
    this.resetInstance();

    let globalColorIDToRestore = 5; // "Blanc" (ID 5) by default, update if an other one is found below.

    for (let i = 0; i < configuration.length; i++) {
      if (configuration[i].box.color != null) {
        globalColorIDToRestore = configuration[i].box.color;
      }

      this.newBox(configuration[i].box.width, configuration[i].box.height);

      for (const accessory of configuration[i].content) {
        this.newContent(i, accessory.name);
        this.updateContentHeight(i, accessory.id, accessory.height);
      }
    }

    this.newGlobalColor(globalColorIDToRestore);
  }

  newBox(width, height) {
    const newBox = {
      group: new THREE.Group(),
      obj: this.assets.assets3D[height][width].clone(),
      width: width,
      height: height,
      content: [],
      position: this.content.length,
      hitBox: null,
    };

    newBox.hitBox = this.createHitBox(newBox);
    newBox.group.position.x = this.getXAtID(this.content.length);
    newBox.obj.material = this.getDefaultMaterial();

    this.content.push(newBox);
    newBox.group.add(newBox.obj);
    newBox.group.add(newBox.hitBox);
    this.scene.add(newBox.group);
  }

  createHitBox(box) {
    const width = box.width / 100;
    const height = box.height / 100;
    const hitBox = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.48), new THREE.MeshBasicMaterial());
    hitBox.position.set(width / 2, height / 2, 0.24);
    hitBox.visible = false;

    return hitBox;
  }

  removeBox(boxId) {
    this.content[boxId].group.clear();
    this.content.splice(boxId, 1);
    this.shiftBoxes(boxId);

    for (let i = boxId; i < this.content.length; i++) {
      this.content[i].position = i;
    }
  }

  shiftBoxes(startId) {
    for (let i = startId; i < this.content.length; i++) {
      this.content[i].group.position.x = this.getXAtID(i);
    }
  }

  // UPDATES BOXES //
  scaleBox(id, updatedParameter, value) {
    // Remove Mesh
    this.content[id].group.clear();

    // Update Mesh
    this.content[id][updatedParameter] = value;
    this.content[id].obj = this.assets.assets3D[this.content[id].height][this.content[id].width].clone();
    this.content[id].hitBox = this.createHitBox(this.content[id]);
    this.content[id].obj.material = this.getDefaultMaterial();

    // Update Children
    this.rescaleChildren(id);

    // Add to the scene
    this.content[id].group.add(this.content[id].obj);
    this.content[id].group.add(this.content[id].hitBox);

    // Recalculate Right boxes
    this.shiftBoxes(id);
  }

  rescaleChildren(id) {
    for (const child of this.content[id].content) {
      child.obj = this.assets.assets3D[child.type][this.content[id].width].clone();
      child.obj.position.y = child.height;
      child.obj.material = this.getDefaultMaterial();

      this.content[id].group.add(child.obj);
    }
  }

  //-----------//
  // ACCESSORY //
  //-----------//
  newContent(id, type, height = 0.0) {
    const newContent = {
      obj: null,
      height: parseInt(height),
      type: type,
    };

    newContent.obj = this.assets.assets3D[type][this.content[id].width].clone();
    newContent.obj.position.y = parseInt(height);
    newContent.obj.material = this.getDefaultMaterial();

    // Push Elements
    this.content[id].content.push(newContent);
    this.content[id].group.add(newContent.obj);
  }

  updateContentHeight(boxID, contentId, value) {
    value = value / 100.0; // Transform the value to meters instead of centimeters

    this.content[boxID].content[contentId].obj.position.y = value;
    this.content[boxID].content[contentId].height = value;
  }

  removeAccessory(boxId, accessoryID) {
    let component = this.content[boxId].content[accessoryID].obj;

    this.content[boxId].content.splice(accessoryID, 1);

    // Remove the accessory
    this.content[boxId].group.remove(component);
    component.geometry.dispose();
    component.material.dispose();
    component = undefined;
  }

  //-------//
  // COLOR //
  //-------//
  newGlobalColor(colorID) {
    this.defaultMap = this.assets.assets2D[colorID].map;

    for (const elem of this.content) {
      elem.obj.material = this.getDefaultMaterial();

      for (const child of elem.content) {
        child.obj.material = this.getDefaultMaterial();
      }
    }
  }

  //----------//
  // MATERIAL //
  //----------//
  getDefaultMaterial() {
    const matMat = this.defaultMap;
    matMat.wrapS = THREE.RepeatWrapping;
    matMat.wrapT = THREE.RepeatWrapping;
    matMat.repeat.set(10, 1);

    return new THREE.MeshStandardMaterial({
      map: matMat,
      transparent: true,
    });
  }

  //-------//
  // UTILS //
  //-------//
  getXAtID(id) {
    let x = 0;
    for (let i = 0; i < id; i++) {
      x += this.content[i].width;
    }
    return x / 100;
  }
}
